import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single configured axios instance — sends cookies on every request
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// 401 auto-refresh interceptor (single-flight)
// ---------------------------------------------------------------------------
// Access tokens expire after 15 minutes. If a request fails with 401 we silently
// call /auth/refresh (which mints a fresh access_token cookie from the long-lived
// refresh_token cookie) and replay the original request. Multiple concurrent
// 401s coalesce onto a single refresh call so we don't hammer the backend.
let _refreshInFlight = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    if (!response || response.status !== 401 || !config) {
      return Promise.reject(error);
    }
    // Don't try to refresh the refresh endpoint itself, or login/register/logout.
    const url = (config.url || "").toString();
    if (
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/logout")
    ) {
      return Promise.reject(error);
    }
    // Prevent infinite retry loops
    if (config._retry) {
      return Promise.reject(error);
    }
    config._retry = true;

    try {
      if (!_refreshInFlight) {
        _refreshInFlight = apiClient
          .post("/auth/refresh")
          .finally(() => {
            // Clear the in-flight slot once the refresh settles (success or fail)
            // so the NEXT 401 can trigger a fresh refresh.
            setTimeout(() => { _refreshInFlight = null; }, 0);
          });
      }
      await _refreshInFlight;
      // Replay the original request with the new cookie
      return apiClient(config);
    } catch (refreshErr) {
      // Refresh itself failed — user must log back in. Bubble the original 401.
      _refreshInFlight = null;
      return Promise.reject(error);
    }
  }
);

export { API };
export default apiClient;

/**
 * Convert FastAPI error `detail` (string | array | object) into a
 * user-friendly string. FastAPI 422 returns an array of validation errors
 * which React cannot render directly — must always coerce to string.
 *
 * v23.13 — Returns "" (empty) when there is nothing useful so callers'
 * `formatApiErrorDetail(...) || "Failed to load X"` fallback strings actually
 * take effect (previously this always returned a generic toast that masked the
 * caller's specific fallback). Also strips Cloudflare/proxy HTML responses.
 */
export function formatApiErrorDetail(detail) {
  if (detail == null || detail === "") return "";
  if (typeof detail === "string") {
    // Cloudflare 520/502 + nginx HTML pages — never show raw markup to users
    const trimmed = detail.trim();
    if (trimmed.startsWith("<") || trimmed.includes("<!DOCTYPE") || trimmed.includes("<html")) {
      return "Service temporarily unavailable — please try again in a moment.";
    }
    // Cloudflare 520/502 JSON-shaped detail ("The origin web server returned an
    // invalid or incomplete response to Cloudflare ...") — substitute friendly copy
    const lower = trimmed.toLowerCase();
    if (lower.includes("origin web server") && lower.includes("cloudflare")) {
      return "Service temporarily unavailable — please try again in a moment.";
    }
    return detail;
  }
  if (Array.isArray(detail)) {
    const joined = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" · ");
    return joined;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "";
}
